import { AuthenticatedRequestsClient } from '@services/AuthenticatedRequestsClient';
import AtCoreClient from 'IhmAssociateToolsCoreService-coral-javascript-clients/jquery-client';
import { EnvironmentDetail } from '@utils/EnvironmentDetailUtils';
import { AppInfoLoader } from '@services/AppInfoLoader';
import { CrossFrameUtils } from '@src/utils';

/**
 * AssociateToolsCoreServiceClientModule
 * This module is responsible for calling IhmAssociateToolsCoreService APIs.
 */
export class IhmAssociateToolsCoreServiceClient {
    private static instance: IhmAssociateToolsCoreServiceClient;
    private readonly client: any;
    private readonly endpoint: string;

    public static getInstance(): IhmAssociateToolsCoreServiceClient {
        if (!IhmAssociateToolsCoreServiceClient.instance) {
            IhmAssociateToolsCoreServiceClient.instance = new IhmAssociateToolsCoreServiceClient();
        }
        return IhmAssociateToolsCoreServiceClient.instance;
    }

    /**
     * Assumes an IAM Role and returns a set of AWS Auth credentials.
     *
     * @param {string} storeId
     * @param {string} roleName
     * @return {Promise} AWS Auth credentials. For example:
     * {
     *      "accessKeyId": "{string}",
     *      "expirationTimestamp": {number} - in milliseconds,
     *      "secretAccessKey": "{string}",
     *      "sessionToken": {string}
     * }
     */
    public async assumeUserRole(storeId: string, roleName: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'AssumeUserRole/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                roleName,
            },
        });
    }

    /**
     * Retrieves ALM store extensions for a specific store.
     *
     * @param storeId - The unique identifier of the store.
     * @param extensions - An array of extension names to retrieve.
     * @return {Promise} - Extensions.
     */
    public async getALMStoreExtensions(storeId: string, extensions: string[]): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'GetALMStoreExtensions/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                extensions,
            },
        });
    }

    /**
     * Returns store info from Libby.
     *
     * @param {string} storeId
     * @return {Promise} Store info. For example.
     * {
     *     "storeFamily": "IHM",
     *     "primePricingEnabled": false,
     *     "tags": [
     *       "CAP_ESL",
     *       "CAP_JWO_ENTRY_EXIT",
     *       "CAP_JWO_CAMERA",
     *       "REAL",
     *       "StoneRetail",
     *       "CAP_JWO_LOAD_SENSOR",
     *       "CAP_JWO_RE_AUTH",
     *       "RESTRICTEDACCESS",
     *       "IHM"
     *     ],
     *     "launchDate": "2013-01-01T00:00:00-07:00",
     *     "pickupAddressId": "FYVMTT5FZ4VPMQ27JHXUG12ECGO6YPPUMA1TMUPPY6OGCE4PXTQ2EIA2OXKRXPIW"
     * }
     */
    public async getStoreInfo(storeId: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'GetStoreInfo/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
            },
        });
    }

    /**
     * Returns the treatment assignment for the given store Id and AppConfig details.
     *
     * @param {string} storeId
     * @param {string} featureId: weblab featureId
     * @param {string} storeFamily
     * eg: IHM_AX_180487
     *
     * @returns {map} if the feature is enabled or not.
     * eg. {"treatmentAssignment": {"featureEnabled": "true"}}
     */
    public async getTreatmentAssignment(storeId, featureId, storeFamily = null): Promise<any> {
        return this.sendRequest({
            url: this.replaceSINEndpointWithPDXIfNeeded(this.endpoint) + 'GetTreatmentAssignment/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                featureId,
                storeFamily,
            },
        });
    }

    /**
     * Returns user Alias.
     * In case of IIB Authentication, the user alias will be fetched from IIB token
     * it will return a promise which directly resolves to the user alias.
     *
     * In case of non-IIB Authentication it will call ATCore GetUserAlias API and retruns
     * Promise and the user alias will be a field in the API response body.
     *
     * @param {string} storeId
     * @return {Promise} user alias. For example
     */
    public async getUserAlias(storeId: string): Promise<any> {
        if (EnvironmentDetail.isIIBAuthN()) {
            return {
                body: {
                    userAlias: await CrossFrameUtils.sendMessageToOtherFrame({
                        frame: window.top,
                        message: {
                            method: 'getUserAliasFromIIBTokenListener',
                            data: {},
                        },
                    }),
                },
            };
        }
        return this.sendRequest({
            url: this.endpoint + 'GetUserAlias/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
            },
        });
    }

    /**
     * Returns the RBAC roles for the currently authenticated user from the IIB token's
     * cognito:groups claim. Only available when using IIB authentication.
     *
     * For example:
     *   {
     *     "IHM":    ["IHM_1P_Associate"],
     *     "WFM":    ["WFM_1P_Automation_Operator", "WFM_1P_Automation_Admin"],
     *     "MENDEL": ["MENDEL_1P_ClientOwner"]
     *   }
     *
     * @return {Promise<Record<string, string[]>>} map of clientId -> roles array.
     *   Resolves to an empty object when not using IIB authentication or when the claim is absent.
     */
    public async getUserRoles(): Promise<Record<string, string[]>> {
        if (EnvironmentDetail.isIIBAuthN()) {
            return CrossFrameUtils.sendMessageToOtherFrame<Record<string, string[]>>({
                frame: window.top,
                message: {
                    method: 'getUserRolesFromIIBTokenListener',
                    data: {},
                },
            });
        }
        return {};
    }

    /**
     * Returns the shopperId (Seller Central ID) for the current associate.
     * In IIB authentication, reads the custom:shopperId claim from the IIB token.
     * @return shopperId string or undefined if not available
     */
    public async getShopperId(): Promise<string | undefined> {
        if (EnvironmentDetail.isIIBAuthN()) {
            const shopperId = await CrossFrameUtils.sendMessageToOtherFrame({
                frame: window.top,
                message: { method: 'getShopperIdFromIIBTokenListener', data: {} },
            });
            return typeof shopperId === 'string' ? shopperId : undefined;
        }
        return undefined;
    }

    /**
     * Returns Zone config for a store.
     *
     * @param {string} storeId
     * @return {Promise} zone config.
     */
    public async getZoneConfigs(storeId: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'GetZoneConfigs/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
            },
        });
    }

    /**
     * Returns User context for a user at a store.
     *
     * @param {string} storeId
     * @param {string} userId
     * @return {Promise} user context.
     */
    public async getUserContext(storeId: string, userId: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'GetUserContext/' + storeId,
            method: 'GET',
            queryParams: {
                storeId,
                userId,
            },
        });
    }

    /**
     * Private Constructor setting the AtCoreService client and its endpoint
     * @private
     */
    private constructor() {
        this.client = AtCoreClient;
        this.client.config.overrideDomain(EnvironmentDetail.getDomain());
        this.client.config.overrideRealm(EnvironmentDetail.getRealm());
        this.endpoint = this.client.config.endpoint();
    }

    /**
     * Makes the call to the abstract authenticatedRequestsClient.
     * @param options call parameters.
     * @returns {*} Output from the call (from the underlying service)
     */
    private async sendRequest(options): Promise<any> {
        let queryParamString = this.buildQueryParamString(options.queryParams);
        try {
            const config = await AppInfoLoader.getAppConfig();
            queryParamString = queryParamString + '&toolName=' + config.toolName;
        } catch (error) {
            console.error(error);
        }
        options.url = options.url + queryParamString;
        delete options.queryParams;
        return AuthenticatedRequestsClient.crossFrameRequestRaw(options);
    }

    /**
     * Converts queryParam json object to queryParam string.
     * Handles both single values and arrays.
     *
     * @param queryParams Object containing query parameters.
     * @return {string} Formatted query string.
     */
    private buildQueryParamString(queryParams: Record<string, string | string[]>): string {
        const queryParts: string[] = [];

        Object.keys(queryParams).forEach((key) => {
            const value = queryParams[key];
            if (Array.isArray(value)) {
                // Handle array values
                value.forEach((item) => {
                    queryParts.push(`${key}=${item}`);
                });
            } else {
                // Handle single values
                queryParts.push(`${key}=${value}`);
            }
        });

        return queryParts.length ? `?${queryParts.join('&')}` : '';
    }

    /**
     * Replaces the SIN endpoint for ATCore API Client with the corresponding PDX endpoint.
     * For other regions it won't change the endpoint. This is done because weblab API support isn't there for SIN
     * Eg input -  https://api.ap-southeast-1.gamma.atcoreservice.ihm.amazon.dev
     * Corresponding output -  https://api.us-west-2.gamma.atcoreservice.ihm.amazon.dev
     * Reference tt: https://t.corp.amazon.com/D86199797/information
     * @param endpoint
     * @private
     */
    private replaceSINEndpointWithPDXIfNeeded(endpoint: string): string {
        console.log('replaceSINEndpointWithPDXIfNeeded called for region: ', EnvironmentDetail.getRealm());
        return endpoint.replace('ap-southeast-1', 'us-west-2');
    }
}
